import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { Box, Button, Flex, Grid, Text } from 'theme-ui';
import SizeChart from '~/components/Product/SizeChart';
import { Step } from '../ProductCard';
import CloseV2 from '~/assets/images/icons/closeV2.svg';
import { graphql, useStaticQuery } from 'gatsby';

type Props = {
  product: Queries.ProductCardFragmentFragment;
  children: React.ReactNode;
  isOpen: boolean;
  currentStep: Step;
  handleClose: () => void;
  selectedColor: Queries.OptionValuesFragmentFragment | null;
  selectedVariant: Queries.VariantDetailsFragmentFragment | null;
};

const AnimatedGrid = motion(Grid);

const TrayContainer = ({
  product,
  children,
  isOpen,
  currentStep,
  handleClose,
  selectedColor,
  selectedVariant,
}: Props) => {
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);
  const trayCopy = {
    variant: 'Select Size',
    'add-to-cart': 'Add to Cart',
    'out-of-stock': 'Out of Stock',
    'added-to-cart': 'Added to cart',
    'stock-request': 'Notify me when available',
  };

  const { contentfulNotification: defaultShippingNote } = useStaticQuery(graphql`
    query DefaultShippingNote {
      contentfulNotification(slug: { eq: "quickshop-default-shipping-note" }) {
        description {
          childMarkdownRemark {
            html
          }
        }
      }
    }
  `);

  return (
    <AnimatePresence>
      {isOpen && (
        <AnimatedGrid
          layout
          key="tray"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          transition={{ duration: 0.3, type: 'tween' }}
          style={{ originX: 1, originY: 1, inset: 'auto 0 0 0' }}
          sx={{
            isolation: 'isolate',
            border: 'medium solid transparent',
            borderRadius: '20px',
            position: 'absolute',
            padding: '1.6rem',
            backgroundColor: 'white',
            top: 0,
            willChange: 'transform, opacity, contents',
            ':after': {
              content: '""',
              position: 'absolute',
              top: '0px',
              right: '0px',
              willChange: 'transform, opacity, contents',
              border: 'thin solid black',
              borderRadius: '20px',
              width: '100%',
              height: '100%',
              zIndex: -1,
            },
          }}
        >
          <Grid sx={{ gap: '0.8rem' }}>
            <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Text as="h4" variant="text.button">
                {trayCopy[currentStep]}
              </Text>
              <Button variant="bare" onClick={handleClose} sx={{ svg: { width: '1rem', height: '1rem' } }}>
                <CloseV2 />
              </Button>
            </Flex>
            {selectedColor ? (
              <Flex sx={{ alignItems: 'center', gap: '0.8rem' }}>
                <Text sx={{ fontSize: '1.2rem', lineHeight: 1.3, fontWeight: 400, color: 'UIGray2' }}>
                  {selectedColor.presentation}
                </Text>
                <Box
                  sx={{
                    backgroundColor: selectedColor.alternateRendering,
                    width: '0.8rem',
                    height: '0.8rem',
                    borderRadius: '50%',
                    position: 'relative',
                    overflow: 'hidden',
                    '::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: selectedColor.alternateRendering2,
                      clipPath: 'polygon(100% 0, 0% 100%, 100% 100%)',
                    },
                  }}
                />
              </Flex>
            ) : null}
          </Grid>
          <Flex
            sx={{
              gap: '1.6rem',
              flexWrap: 'wrap',
            }}
          >
            {children}
          </Flex>

          <Flex sx={{ justifyContent: 'space-between', gap: '2.4rem' }}>
            <Text
              sx={{
                fontSize: '1.2rem',
                lineHeight: 1.3,
                fontWeight: 400,
                textAlign: 'center',
                color: 'UIGray2',
                p: { m: 0 },
              }}
              dangerouslySetInnerHTML={
                selectedVariant?.optionalShippingNote
                  ? undefined
                  : { __html: defaultShippingNote.description.childMarkdownRemark.html }
              }
            >
              {selectedVariant?.optionalShippingNote}
            </Text>
            <Button
              type="button"
              onClick={() => setIsSizeChartOpen(true)}
              variant="bare"
              sx={{
                textDecoration: 'underline',
                display: 'block',
                color: 'black',
                minHeight: '0',
                lineHeight: 1.3,
                fontSize: '1.2rem',
                fontWeight: 400,
                flex: '0 0 auto',
              }}
            >
              Size Guide
            </Button>
          </Flex>
        </AnimatedGrid>
      )}
      <SizeChart isOpen={isSizeChartOpen} sizeChart={product.sizeChart} onDismiss={() => setIsSizeChartOpen(false)} />
    </AnimatePresence>
  );
};

export default TrayContainer;
